/**
 * @file GenericTooltip.tsx
 * @description
 * -----------------------------------------------------
 * Componente genérico de tooltip para reutilizar lógica de apertura y cierre
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-11-25
 * @author Correcta
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 */

"use client";

import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, HTMLProps } from "react";
import { createPortal } from "react-dom";
import { CircleQuestionMark } from "lucide-react";
import clsx from "clsx";
interface GenericTooltipProps extends HTMLProps<HTMLElement> {
  title?: string;
  trigger?: React.ReactNode;
  onOpenChange?: (_isOpen: boolean) => void;
  offset?: number;
  maxWidth?: React.CSSProperties["maxWidth"];
  /** Nombre accesible del botón disparador del tooltip. */
  triggerAriaLabel?: string;
}
const GenericTooltip: React.FC<GenericTooltipProps> = ({
  title,
  trigger,
  children,
  onOpenChange,
  className,
  offset = 12,
  maxWidth,
  triggerAriaLabel,
  ...props
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const closeTooltip = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) return prev;
      onOpenChange?.(false);
      return false;
    });
  }, [onOpenChange]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) && !dropdownRef.current?.contains(event.target as Node)) {
        closeTooltip();
      }
    };
    const handleKeyDown = (event_0: KeyboardEvent) => {
      if (event_0.key === "Escape") {
        closeTooltip();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeTooltip]);
  const openTooltip = useCallback(() => {
    setIsOpen(prev_0 => {
      if (prev_0) return prev_0;
      onOpenChange?.(true);
      return true;
    });
  }, [onOpenChange]);
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !dropdownRef.current) return;
    const updatePosition = () => {
      if (!triggerRef.current || !dropdownRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const gap = offset;
      let top = triggerRect.top - dropdownRect.height - gap;
      let placeBelow = false;
      if (top < 8) {
        top = triggerRect.bottom + gap;
        placeBelow = true;
      }
      const preferredLeft = triggerRect.left + triggerRect.width / 2 - 24;
      const left = Math.max(8, preferredLeft);
      const arrowLeft = Math.min(Math.max(18, triggerRect.left + triggerRect.width / 2 - left - 10), dropdownRect.width - 28);
      setDropdownStyle({
        position: "fixed",
        top,
        left,
        maxWidth,
        ["--generic-tooltip-arrow-left" as string]: `${arrowLeft}px`,
        ["--generic-tooltip-arrow-border" as string]: placeBelow ? "top" : "bottom"
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, maxWidth, offset]);
  const toggleOpen = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsOpen(prev_1 => {
      const next = !prev_1;
      onOpenChange?.(next);
      return next;
    });
  }, [onOpenChange]);
  return <article ref={tooltipRef} className={clsx("generic-tooltip", className)} onBlurCapture={event_1 => {
    const nextFocus = event_1.relatedTarget as Node | null;
    if (!nextFocus || !event_1.currentTarget.contains(nextFocus)) {
      closeTooltip();
    }
  }} {...props}>
      <button ref={triggerRef} type="button" className="generic-tooltip__trigger" onClick={toggleOpen} onFocusCapture={openTooltip} onMouseEnter={openTooltip} onMouseLeave={closeTooltip} aria-label={triggerAriaLabel} aria-expanded={isOpen} aria-haspopup="dialog">
        {trigger ? trigger : <CircleQuestionMark size={14} className="generic-tooltip__icon" aria-hidden="true" />}
      </button>

      {isOpen && createPortal(<section ref={dropdownRef} className={clsx("generic-tooltip__dropdown", className)} style={dropdownStyle} onMouseLeave={closeTooltip}>
            {title && <h3 className="generic-tooltip__title">{title}</h3>}
            <section className="generic-tooltip__content">{children}</section>
          </section>, document.body)}
    </article>;
};
export default GenericTooltip;